import { MutableState, Tools } from 'final-form';
import { PricebookBundle } from '../models/PricebookData';
import { Dayjs } from 'dayjs';
import { PrimitiveType } from '../components/DataTable/types';
import { AddressInfo } from '../models/OrganizationData';

const convertDollarToNumber = (dollar: string) => {
  return parseFloat(dollar.replace(/[$,]/g, ''));
};

const toDateKey = (date: Dayjs): string => {
  return date.format('YYYY-MM-DD');
};

const formatDateMMDDYYYY = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  return month + '/' + day + '/' + year;
};

const formatDateMDYY = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${String(date.getUTCFullYear()).slice(-2)}`;
};

const formatDateMMDDYY = (dateString: string): string => {
  const date = new Date(dateString);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const formatDateYYYYMMDD = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  return year + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
};

const formatTimestamp = (dateString: string): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }
  return formatter.format(date);
};

const trimPhoneNumber = (value: string) => {
  return value.replace(/[^\d]/g, '');
};

function cleanWholeNumberStr(strVal: string, min?: number, max?: number): number | undefined {
  if (!strVal) return undefined;
  const numVal = parseInt(strVal.replace(/[^\d.]/g, ''), 10) || 0;
  if (min != null && numVal < min) return min;
  if (max != null && numVal > max) return max;
  return numVal;
}

function cleanDecimalStr(
  strVal: string,
  min?: number,
  max?: number,
  decimalPlaces?: number,
): string {
  if (!strVal || strVal === '.') return strVal;

  let cleaned = strVal.replace(/[^\d.]/g, '');

  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  const normalizedParts = cleaned.split('.');
  if (decimalPlaces != null && normalizedParts.length === 2) {
    cleaned = normalizedParts[0] + '.' + normalizedParts[1].slice(0, decimalPlaces);
  }

  const numVal = parseFloat(cleaned) || 0;
  if (min != null && numVal < min) return String(min);
  if (max != null && numVal > max) return String(max);

  return cleaned;
}

const formatPhoneNumber = (value: string | undefined) => {
  if (!value) return undefined;
  const currentValue = trimPhoneNumber(value);
  const cvLength = currentValue.length;
  if (cvLength < 4) {
    return currentValue;
  }
  if (cvLength < 7) {
    return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
  }
  return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6, 10)}`;
};

const formatAddress = (address?: AddressInfo | null): string => {
  if (!address?.streetAddress) return '';

  const { streetAddress, city, state, zip } = address;

  const cityState = city && state ? `${city}, ${state.toUpperCase()}` : '';

  const addressParts = [];
  if (streetAddress && streetAddress.trim() !== '') {
    addressParts.push(streetAddress.trim());
  }
  if (cityState && cityState !== '') {
    addressParts.push(cityState);
  }
  if (zip && zip !== '') {
    addressParts.push(zip);
  }

  return addressParts.join(' ');
};

const filterByPayer = (pricebookOptions: PricebookBundle[], paidBy: string | undefined) => {
  if (paidBy) {
    const filtered = pricebookOptions.filter(pricebook => pricebook.pricebook.paidBy === paidBy);
    return filtered;
  }
  return pricebookOptions;
};

const formatDollars = (amount: number, decimalPlaces?: number): string => {
  const formatter = new Intl.NumberFormat('en-us', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimalPlaces,
  });
  return formatter.format(amount);
};

const formatTwoDecimalPlaces = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
};

// will display whole number if decimals are ".00"
const formatDecimalOrWholeDollars = (value: number | null | undefined): string => {
  if (value == null) return '0';

  const val = Number(value);
  if (!Number.isFinite(val)) return '0';

  const noCents = Math.round(val * 100) % 100 === 0;

  return noCents ? formatNumber(Math.round(val)) : formatTwoDecimalPlaces(val);
};

const formatNumber = (x: number): string => {
  const formatter = new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 0 });
  return formatter.format(x);
};

// * https://github.com/jquense/yup
// managing validation errors for yup
const reduceValidationError = (validationError: any) =>
  validationError.inner.reduce((total: any, currentValue: any) => {
    total[currentValue.path] = currentValue.message;
    return total;
  }, {});

const validateEmail = (value: string) => {
  if (!/\S+@\S+\.\S+/.test(value)) {
    return 'Invalid Email';
  }
};

const mutateFieldValue = (args: any[], state: MutableState<any>, tools: Tools<any>) => {
  const [fieldName, value] = args;
  tools.changeValue(state, fieldName, () => value);
};

const convertToTitleCase = (str: string) => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/Us/g, 'US');
};

const pluralize = (count: number, singular: string, plural = `${singular}s`) => {
  return Math.abs(count) === 1 ? singular : plural;
};

const isDate = (value: any): boolean => {
  if (typeof value !== 'string') return false;

  const dateFormats = [
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/, // YYYY-MM-DDTHH:MM:SSZ (ISO 8601)
  ];

  if (!dateFormats.some(format => format.test(value))) {
    return false;
  }

  const date = new Date(value);
  return !isNaN(date.getTime());
};

const isDollar = (value: any): boolean => {
  if (typeof value !== 'string') return false;
  return value.startsWith('$') && !isNaN(convertDollarToNumber(value));
};

const isPrimitive = (value: any): value is PrimitiveType => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'object'
  );
};

const toPascalCase = (str: string): string => {
  return (
    str.charAt(0).toUpperCase() +
    str
      .slice(1)
      .toLowerCase()
      .replace(/_./g, match => match[1].toUpperCase())
  );
};

const sumPropertyAcrossArrayItems = <T extends { [key: string]: unknown }>(
  array: T[],
  property: keyof T,
): number => {
  if (!Array.isArray(array)) return 0;

  return array.reduce((sum, item) => {
    if (item && typeof item === 'object' && property in item) {
      const value = item[property];
      return sum + (typeof value === 'number' ? value : 0);
    }
    return sum;
  }, 0);
};

export {
  convertDollarToNumber,
  toDateKey,
  formatDateMMDDYYYY,
  formatDateMDYY,
  formatDateMMDDYY,
  formatDateYYYYMMDD,
  formatTimestamp,
  formatPhoneNumber,
  trimPhoneNumber,
  cleanWholeNumberStr,
  cleanDecimalStr,
  formatAddress,
  filterByPayer,
  formatDollars,
  formatTwoDecimalPlaces,
  formatDecimalOrWholeDollars,
  formatNumber,
  reduceValidationError,
  validateEmail,
  mutateFieldValue,
  convertToTitleCase,
  pluralize,
  isDate,
  isDollar,
  isPrimitive,
  toPascalCase,
  sumPropertyAcrossArrayItems,
};
